// ============================================
// pages/data-entry/FarmerPaymentsSection.tsx
// Installment ledger nested inside FarmersForm — credit-first, pay in N parts.
// Renders only in edit mode (after a district_farmers record exists).
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, CheckCircle, XCircle, Wallet } from 'lucide-react';
import { farmerPaymentsAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';

interface FarmerPayment {
  id: number;
  district_farmers_id: number;
  paid_on: string;
  amount_rs: number;
  mode: string;
  utr_no?: string;
  bank_name?: string;
  remarks?: string;
}

interface Props {
  districtFarmersId: number;
  costOfProcuredQty: number;
  canEdit: boolean;
  // Called whenever totals change so the parent form can refresh its derived state.
  onChange?: (totalReleased: number, balanceToRelease: number) => void;
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyDraft: Omit<FarmerPayment, 'id' | 'district_farmers_id'> = {
  paid_on: todayISO(),
  amount_rs: 0,
  mode: 'neft',
  utr_no: '',
  bank_name: '',
  remarks: '',
};

export const FarmerPaymentsSection: React.FC<Props> = ({
  districtFarmersId,
  costOfProcuredQty,
  canEdit,
  onChange,
}) => {
  const [rows, setRows] = useState<FarmerPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyDraft);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const totalReleased = rows.reduce((s, r) => s + num(r.amount_rs), 0);
  const balanceToRelease = Math.max(costOfProcuredQty - totalReleased, 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await farmerPaymentsAPI.list(districtFarmersId);
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load payments' });
    } finally {
      setLoading(false);
    }
  }, [districtFarmersId]);

  useEffect(() => {
    if (districtFarmersId) load();
  }, [districtFarmersId, load]);

  // Notify parent on every recompute so the auto-calculated panel stays in sync.
  useEffect(() => {
    onChange?.(totalReleased, balanceToRelease);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalReleased, balanceToRelease]);

  const handleAdd = async () => {
    if (!draft.amount_rs || draft.amount_rs <= 0) {
      setMessage({ type: 'error', text: 'Amount must be greater than zero' });
      return;
    }
    if (!draft.paid_on) {
      setMessage({ type: 'error', text: 'Paid date is required' });
      return;
    }
    setAdding(true);
    setMessage(null);
    try {
      await farmerPaymentsAPI.create(districtFarmersId, draft);
      setDraft({ ...emptyDraft, paid_on: todayISO() });
      await load();
      setMessage({ type: 'success', text: 'Installment added' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to add installment' });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this installment? The parent record will be re-totaled.')) return;
    setDeletingId(id);
    setMessage(null);
    try {
      await farmerPaymentsAPI.remove(id);
      await load();
      setMessage({ type: 'success', text: 'Installment removed' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to delete' });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (s?: string) => {
    if (!s) return '--';
    try {
      return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return s;
    }
  };

  return (
    <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-green-600" />
          Payment Installments
          <span className="text-xs font-normal text-gray-500">
            ({rows.length} {rows.length === 1 ? 'payment' : 'payments'})
          </span>
        </h3>
        <div className="text-right text-sm">
          <div className="text-gray-500">
            Released: <span className="font-semibold text-green-700">{formatAmount(totalReleased)}</span>
          </div>
          <div className="text-gray-500">
            Balance to release: <span className="font-semibold text-orange-600">{formatAmount(balanceToRelease)}</span>
          </div>
        </div>
      </div>

      {message && (
        <div
          className={`mb-3 flex items-center gap-2 p-2.5 rounded-lg text-xs ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Existing installments */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">#</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Paid On</th>
              <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Mode</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">UTR / Ref</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Bank</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Remarks</th>
              {canEdit && <th className="px-3 py-2 text-center text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Act</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin inline" /> Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-gray-400 text-sm">
                  No payments recorded yet. Use the row below to add the first installment.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{formatDate(r.paid_on)}</td>
                  <td className="px-3 py-2 text-xs text-right font-medium text-gray-900">
                    {formatAmount(num(r.amount_rs))}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 uppercase">{r.mode}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 font-mono">{r.utr_no || '--'}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{r.bank_name || '--'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 max-w-[180px] truncate" title={r.remarks || ''}>
                    {r.remarks || '--'}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleDelete(r.id)}
                        disabled={deletingId === r.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                      >
                        {deletingId === r.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Trash2 className="w-3 h-3" />
                        )}
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add row */}
      {canEdit && (
        <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Paid On</label>
              <input
                type="date"
                value={draft.paid_on}
                onChange={(e) => setDraft({ ...draft, paid_on: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Amount (Rs)</label>
              <input
                type="number"
                step="0.01"
                value={draft.amount_rs || ''}
                onChange={(e) => setDraft({ ...draft, amount_rs: parseFloat(e.target.value) || 0 })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs text-right focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Mode</label>
              <select
                value={draft.mode}
                onChange={(e) => setDraft({ ...draft, mode: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500"
              >
                <option value="neft">NEFT</option>
                <option value="rtgs">RTGS</option>
                <option value="imps">IMPS</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">UTR / Ref</label>
              <input
                type="text"
                value={draft.utr_no || ''}
                onChange={(e) => setDraft({ ...draft, utr_no: e.target.value })}
                placeholder="UTR / cheque no"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Bank</label>
              <input
                type="text"
                value={draft.bank_name || ''}
                onChange={(e) => setDraft({ ...draft, bank_name: e.target.value })}
                placeholder="Bank name"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="md:col-span-1">
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Remarks</label>
              <input
                type="text"
                value={draft.remarks || ''}
                onChange={(e) => setDraft({ ...draft, remarks: e.target.value })}
                placeholder="Optional"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <button
                onClick={handleAdd}
                disabled={adding}
                className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded shadow-sm disabled:opacity-50"
              >
                {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmerPaymentsSection;
